import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesAction, fundsAction, globalAction } from 'common/actions';
import { firmSummaryUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { DialogContextValues } from 'common/types/dialogContext';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import { FundFormSchema } from 'pages/Funds/forms/FundDialogForm/hooks/useFundForm';
import { useCreateFund, useDeleteFund, useUpdateFund } from 'services/hooks/funds';
import { getArrayValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { UseFund } from './types';

const DELETE_LABEL = 'Delete';
const DIALOG_TITLE = 'Confirmation';

const getFundRedirectUrl = (firmSlug: string, fundId?: number | null) =>
  firmSummaryUrl(firmSlug, undefined, fundId ? String(fundId) : undefined);

const useFund: UseFund = params => {
  const { firmSlugParam, onSuccess } = params;

  const [storeValue, dispatch] = useStore() as unknown as UseStoreValues;
  const { firmId, fundInfo, fundList } = storeValue;

  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;

  const navigate = useNavigate();

  const { mutateAsync: createFund } = useCreateFund();

  const { mutateAsync: updateFund } = useUpdateFund();

  const { mutateAsync: deleteFund } = useDeleteFund();

  // Create
  const handleCreateFund = useCallback(
    async (fund: FundFormSchema) => {
      dispatch(globalAction.showLoadingProgress(true));

      const createFundResponse = await createFund(
        { fund: { ...fund, firm: getNumberValue(firmId) } },
        {
          onError: () => {
            dispatch(globalAction.showLoadingProgress(false));
          },
        }
      );

      const { fund: createdFund } = getObjectValue(createFundResponse);

      if (createdFund) {
        // Keep the legacy fund store in sync while the query refetches.
        dispatch?.(fundsAction.setFundInfo(getObjectValue(createdFund)));
        dispatch?.(
          companiesAction.setFundList(
            fundList ? [...fundList, getObjectValue(createdFund)] : [getObjectValue(createdFund)]
          )
        );
      }

      dispatch(globalAction.showLoadingProgress(false));

      if (onSuccess) {
        onSuccess();
        return;
      }

      return navigate(getFundRedirectUrl(getStringValue(firmSlugParam), createdFund?.id));
    },
    [createFund, dispatch, firmId, firmSlugParam, fundList, navigate, onSuccess]
  );

  // Update
  const handleUpdateFund = useCallback(
    async (fund: FundFormSchema) => {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        const updateFundResponse = await updateFund({
          fund: { ...getObjectValue(fundInfo), ...fund, firm: getNumberValue(firmId) },
        });

        const { fund: updatedFund } = getObjectValue(updateFundResponse);

        if (updatedFund) {
          // Keep the legacy fund store in sync while the query refetches.
          dispatch?.(fundsAction.setFundInfo(getObjectValue(updatedFund)));

          const updatedFundList = [...getArrayValue(fundList)];
          const fundIndex = updatedFundList.findIndex(fundItem => fundItem?.id === updatedFund?.id);
          if (fundIndex !== -1) {
            updatedFundList[fundIndex] = getObjectValue(updatedFund);
          }
          dispatch?.(companiesAction.setFundList(updatedFundList));

          if (onSuccess) {
            onSuccess();
            return;
          }

          return navigate(getFundRedirectUrl(getStringValue(firmSlugParam), updatedFund?.id));
        }

        // If the service returns null/undefined without throwing, keep FormDialog open.
        throw new Error('Update failed: No fund data returned');
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, firmId, firmSlugParam, fundInfo, fundList, navigate, onSuccess, updateFund]
  );

  // Delete
  const handleDeleteFund = useCallback(async () => {
    dispatch(globalAction.showLoadingProgress(true));

    const deleteFundResponse = await deleteFund({ fundId: fundInfo?.id });

    const { fundId } = getObjectValue(deleteFundResponse);

    // Keep the legacy fund store in sync while the query refetches.
    if (fundId) {
      dispatch?.(fundsAction.setFundList(fundList ? fundList.filter(fund => fund.id !== fundId) : null));
    }

    dispatch(globalAction.showLoadingProgress(false));

    return navigate(firmSummaryUrl(getStringValue(firmSlugParam)));
  }, [deleteFund, dispatch, firmSlugParam, fundInfo, fundList, navigate]);

  const confirmDeleteFund = useCallback(() => {
    showDialog({
      wrapper: ConfirmationDialog,
      content: () => <ConfirmationDelete itemName={`Fund: ${fundInfo?.name}`} />,
      actions: [
        {
          label: DELETE_LABEL,
          variant: 'contained',
          type: 'danger',
          callback: handleDeleteFund,
        },
      ],
      wrapperProps: {
        title: DIALOG_TITLE,
      },
    });
  }, [fundInfo, handleDeleteFund, showDialog]);

  return {
    confirmDeleteFund,
    handleCreateFund,
    handleUpdateFund,
  };
};

export default useFund;
